import moment from 'moment';
import get from 'lodash/get';
import uniqBy from 'lodash/uniqBy';
import PropTypes from 'prop-types';
import {useState} from 'react';
import 'react-dates/lib/css/_datepicker.css';
import 'react-dates/initialize';
import {SingleDatePicker} from 'react-dates';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimeField } from '@mui/x-date-pickers/TimeField';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import '../scheduling/SelectADate.scss';
import CustomInputLabel from '../common/CustomInputLabel';
import CustomSelect from '../common/CustomSelect';
import FormSectionHeading from '../common/FormSectionHeading';

import {getLocalTimeZone, getTimeZoneOptions} from '../../helpers/scheduling';
import useError from '../../hooks/useError';
import useIsMobile from '../../hooks/useIsMobile';
import { useCreateVacationTimeMutation } from '../../redux/services/vacationTimeApi';


const getDefaultFormValues = () => ({
  start_date: null,
  start_time: '12:00 am',
  end_date: null,
  end_time: '11:59 pm',
  timezone: getLocalTimeZone(),
  decline_new_sessions: true,
  decline_existing_sessions: false,
  is_recurring: false,
  recurring_days: null,
  recurring_start_time: null,
  recurring_end_time: null,
  repeat_every_period_value: null,
  repeat_every_period_unit: null,
});

const TimeZoneSelect = props => (
  <CustomSelect
    {...props}
    options={getTimeZoneOptions()}
  />
);

const handleDatePick = ({setDate, onChange}) => momentObj => {
  setDate(momentObj);
  if (!momentObj) return;
  onChange({target: {value: momentObj.format('MM/DD/yyyy')}});
};

const DatePicker = props => {
  const [date, setDate] = useState(null);
  const [focused, setFocus] = useState(false);
  const {onChange} = props;
  return (
    <SingleDatePicker
      id={props.id}
      date={date}
      onDateChange={handleDatePick({setDate, onChange})}
      focused={!!focused}
      onFocusChange={({focused}) => setFocus(focused)}
      numberOfMonths={1}
      hideKeyboardShortcutsPanel={true}
      noBorder={true}
      minDate={moment()}
    />
  );
};

const handleFieldChange = ({formValues, setFormValues}) => fieldName => e => {
  let value;
  console.log('fieldName', fieldName, e.target.value);
  switch (fieldName) {
    case 'is_recurring':
      value = e.target?.checked;
      break;
    case 'recurring_days':
      value = uniqBy(e.target.value || [], opt => opt.value);
      console.log('RECURRING_DAYS', value);
      break;
    case 'decline_new_sessions':
      value = e.target?.checked || false;
      break;
    default:
      value = get(e, 'target.value', null);
  }
  let newValues = {...formValues, [fieldName]: value};
  if (fieldName === 'is_recurring') {
    if (value) {
      newValues.recurring_start_time = '12:00 am';
      newValues.recurring_end_time = '11:59 pm';
      newValues.repeat_every_period_value = 1;
    } else {
      newValues.recurring_days = null;
      newValues.recurring_start_time = null;
      newValues.recurring_end_time = null;
      newValues.repeat_every_period_value = null;
      newValues.repeat_every_period_unit = null;
    }
  }
  return setFormValues(newValues);
};

const parseFormValues = formValues => ({
  start_time: formValues.is_recurring 
    ? moment().startOf('day').toISOString()
    : moment(
      `${formValues.start_date} ${formValues.start_time}`,
      'MM/DD/yyyy hh:mm a'
    ).toISOString(),
  end_time: formValues.is_recurring 
    ? (formValues.end_date ? moment(formValues.end_date).endOf('day').toISOString() : null)
    : moment(
      `${formValues.end_date} ${formValues.end_time}`,
      'MM/DD/yyyy hh:mm a'
    ).toISOString(),
  timezone: formValues.timezone,
  decline_new_sessions: formValues.decline_new_sessions,
  decline_existing_sessions: formValues.decline_existing_sessions,
  is_recurring: formValues.is_recurring,
  recurring_days: formValues?.recurring_days?.length
    ? formValues.recurring_days.map(opt => opt.value)
    : null,
  recurring_start_time: formValues.recurring_start_time,
  recurring_end_time: formValues.recurring_end_time,
  repeat_every_period_value: formValues.repeat_every_period_value,
  repeat_every_period_unit: formValues.is_recurring ? 'week' : null,
});

const handleAllDayCheck = ({
  start,
  setStartAllDay,
  setEndAllDay,
  formValues,
  setFormValues
}) => e => {
  const checked = get(e, 'target.checked', null);
  if (start) {
    setStartAllDay(checked);
    if (checked) setFormValues({...formValues, start_time: '12:00 am'});
    return;
  }
  setEndAllDay(checked);
  if (checked) setFormValues({...formValues, end_time: '11:59 pm'});
};

const validate = ({setValidation, formValues}) => {
  console.log('validate.formValues', formValues);
  if (!formValues.is_recurring && !formValues.start_date) {
    setValidation('Choose a start date');
    return false;
  }
  if (!formValues.end_date) {
    setValidation('Choose an end date');
    return false;
  }
  if (!formValues.timezone) {
    setValidation('Choose a time zone');
    return false;
  }
  const dateFormat = 'M/D/yyyy h:mm a';
  const startStr = `${formValues.start_date} ${formValues.start_time}`;
  const endStr = `${formValues.end_date} ${formValues.end_time}`;
  const end = moment(endStr, dateFormat);
  if (!formValues.is_recurring && !end.isAfter(startStr)) {
    setValidation('Start time must be before end time.');
    return false;
  }
  return true;
};

const handleSubmit = ({
  createVacationTime,
  vacationTimesQuery,
  setError,
  onSuccess,
}) => formData => {
  const response = createVacationTime(formData);
  if (response.error) return setError({message: 'Error creating vacation time'});
  setError(null);
  if (onSuccess) onSuccess();
};

const VacationTimeForm = props => {
  const defaultFormValues = getDefaultFormValues();
  const [formValues, setFormValues] = useState(defaultFormValues);
  const [validation, setValidation] = useState(null);
  const [startAllDay, setStartAllDay] = useState(true);
  const [endAllDay, setEndAllDay] = useState(true);
  const isMobile = useIsMobile();
  const error = useError();
  const [createVacationTime] = useCreateVacationTimeMutation();
  const onFieldChange = handleFieldChange({formValues, setFormValues});
  const onSubmit = handleSubmit({
    createVacationTime,
    setError: error.create,
    onSuccess: props.onSuccess,
  });
  return (
        <Stack spacing={2} padding={1}>
          {validation
            ? <Alert severity='error'>
              {validation}
            </Alert>
            : null
          }
          <Typography variant='body1'>
            Scheduling away time will block your calendar and prevent patients
            from scheduling during your time away.
          </Typography>
          <FormSectionHeading
            title='Recurring Away time'
          >
          </FormSectionHeading>
          <FormGroup>
            <FormControlLabel
                control={
                  <Checkbox
                      onChange={onFieldChange('is_recurring')}
                      sx={{color: 'text.gray', padding: '3px'}}
                  />
                }
                checked={formValues.is_recurring}
                label='Recurring away time'
            />
          </FormGroup>
          {!formValues.is_recurring && <>
            <FormSectionHeading
              title='Date Range'
              subtitle='Define the date and time range during which you will be out of office.'
            />
            <Stack>
              <CustomInputLabel>
                Start time
              </CustomInputLabel>
              <Stack spacing={2} direction={isMobile ? 'column' : 'row'}>
                <DatePicker
                    id='start_date'
                    label='Start date'
                    onChange={onFieldChange('start_date')}
                />
                <Stack direction='row' spacing={2}>
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <TimeField
                        label={null}
                        placeholder='12:00 AM'
                        style={{maxWidth: '100px'}}
                        disabled={startAllDay}
                        value={
                          formValues.start_time
                              ? moment(formValues.start_time, 'hh:mm a')
                              : null
                        }
                        onChange={momentObj => onFieldChange('start_time')({
                          target: {value: momentObj.format('hh:mm a')},
                        })}
                    />
                  </LocalizationProvider>
                  <Stack justifyContent={"center"}>
                    <FormGroup>
                      <FormControlLabel
                          control={
                            <Checkbox
                                onChange={handleAllDayCheck({
                                  start: true,
                                  setStartAllDay,
                                  setEndAllDay,
                                  formValues,
                                  setFormValues
                                })}
                                sx={{color: 'text.gray', padding: '3px'}}
                            />
                          }
                          checked={startAllDay}
                          label='All day'
                      />
                    </FormGroup>
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
            <Stack>
              <CustomInputLabel>
                End time
              </CustomInputLabel>
              <Stack spacing={2} direction={isMobile ? 'column' : 'row'}>
                <DatePicker
                    id='end_date'
                    onChange={onFieldChange('end_date')}
                />
                <Stack direction='row' spacing={2}>
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <TimeField
                        label={null}
                        placeholder='11:59 PM'
                        style={{maxWidth: '100px'}}
                        disabled={endAllDay}
                        value={
                          formValues.end_time
                              ? moment(formValues.end_time, 'hh:mm a')
                              : null
                        }
                        onChange={momentObj => onFieldChange('end_time')({
                          target: {value: momentObj.format('hh:mm a')},
                        })}
                    />
                  </LocalizationProvider>
                  <Stack justifyContent='center'>
                    <FormGroup>
                      <FormControlLabel
                          control={
                            <Checkbox
                                onChange={handleAllDayCheck({
                                  start: false,
                                  setStartAllDay,
                                  setEndAllDay,
                                  formValues,
                                  setFormValues
                                })}
                                checked={endAllDay}
                                sx={{color: 'text.gray', padding: '3px'}}
                            />
                          }
                          label='All day'
                      />
                    </FormGroup>
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
          </>}
          {
            formValues.is_recurring && <>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <Grid container spacing={1}>
                  <Grid item xs={12} md={6}>
                    <Stack>
                      <CustomInputLabel required>
                        Start time
                      </CustomInputLabel>
                      <TimeField
                        label={null}
                        style={{marginTop: '0px'}}
                        placeholder='12:00 AM'
                        value={
                          formValues.recurring_start_time
                          ? moment(formValues.recurring_start_time, 'hh:mm a')
                          : null
                        }
                        onChange={momentObj => onFieldChange('recurring_start_time')({
                          target: {value: momentObj.format('hh:mm a')},
                        })}
                      />
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Stack>
                      <CustomInputLabel required>
                        End time
                      </CustomInputLabel>
                      <TimeField
                        label={null}
                        style={{marginTop: '0px'}}
                        placeholder='11:59 PM'
                        value={
                          formValues.recurring_end_time
                          ? moment(formValues.recurring_end_time, 'hh:mm a')
                          : null
                        }
                        onChange={momentObj => onFieldChange('recurring_end_time')({
                          target: {value: momentObj.format('hh:mm a')},
                        })}
                      />
                    </Stack>
                  </Grid>
                  <Grid item xs={12}></Grid>
                  <Grid item xs={12} md={6}>
                    <CustomSelect
                      label='Days'
                      multiple
                      required
                      fullWidth
                      size='small'
                      filterSelectedOptions={true}
                      options={[
                        {label: 'Mon', value: 'MON'},
                        {label: 'Tue', value: 'TUE'},
                        {label: 'Wed', value: 'WED'},
                        {label: 'Thu', value: 'THU'},
                        {label: 'Fri',value: 'FRI'},
                        {label: 'Sat', value: 'SAT'},
                        {label: 'Sun',value: 'SUN'},
                      ]}
                      onChange={onFieldChange('recurring_days')}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <CustomSelect
                      label='Repeat every'
                      required
                      fullWidth
                      defaultValue={1}
                      options={[
                        {label: '1 Week', value: 1},
                        {label: '2 Weeks', value: 2},
                        {label: '3 Weeks', value: 3},
                        {label: '4 Weeks', value: 4},
                        {label: '5 Weeks', value: 5},
                        {label: '6 Weeks', value: 6},
                        {label: '7 Weeks', value: 7},
                        {label: '8 Weeks', value: 8},
                      ]}
                      onChange={onFieldChange('repeat_every_period_value')}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Stack>
                      <CustomInputLabel>
                        End Date
                      </CustomInputLabel>
                      <DatePicker
                        label='Ends On'
                        id='end_date'
                        onChange={onFieldChange('end_date')}
                      />
                    </Stack>
                  </Grid>
                </Grid>
              </LocalizationProvider>
            </>
          }
          <TimeZoneSelect
            label='Time Zone'
            defaultValue={defaultFormValues.timezone}
            onChange={onFieldChange('timezone')}
          />
          <Button
            variant='primaryGradient'
            sx={{minWidth: 100}}
            size="medium"
            onClick={() => {
              const valid = validate({setValidation, formValues});
              if (valid) onSubmit(parseFormValues(formValues))
            }}
          >
            Save
          </Button>
        </Stack>
  );
};

VacationTimeForm.propTypes = {
  onSuccess: PropTypes.func,
};

export default VacationTimeForm;
